// ---------------------------------------------------------
// Datei: constructionDocumentationErrors.ts
// Zweck: Übersetzt Fehler beim Laden der Baustellendokumentation (Drive-Ablage) in eine
//        ursachenspezifische Meldung für den Nutzer.
// Kontext: AB#1474 – statt des festen Texts "Baustellendokumentation konnte nicht geladen werden."
//          soll der tatsächliche Grund samt Projekt-Referenz sichtbar sein, damit der Support
//          den betroffenen Deal sofort findet.
// ---------------------------------------------------------
import { isAuthMissingError } from './api/one360';

export interface ConstructionDocumentationLoadErrorContext {
  dealId: string;
  dealName?: string;
}

const GENERIC_AXIOS_MESSAGE = /^(request failed with status code|network error|timeout of|canceled)/i;

const buildReference = ({ dealId, dealName }: ConstructionDocumentationLoadErrorContext): string => {
  const name = dealName?.trim();
  return name ? `Projekt: ${name} (ID ${dealId})` : `Projekt-ID: ${dealId}`;
};

/**
 * Liefert die Nutzermeldung zum Ladefehler oder `null`, wenn kein Toast erscheinen soll
 * (fehlende/abgelaufene Anmeldung – der AuthContext leitet bereits auf /login um).
 */
export const describeConstructionDocumentationLoadError = (
  error: unknown,
  context: ConstructionDocumentationLoadErrorContext,
): string | null => {
  if (isAuthMissingError(error)) return null;

  const status = (error as { status?: number } | null)?.status;
  if (status === 401) return null;

  const rawMessage = error instanceof Error ? error.message.trim() : '';
  const backendMessage = rawMessage && !GENERIC_AXIOS_MESSAGE.test(rawMessage) ? rawMessage : '';

  let reason: string;
  if (status === 403) {
    reason = 'Kein Zugriff auf die Baustellendokumentation dieses Projekts.';
  } else if (status === 404) {
    // Backend unterscheidet "Projekt nicht gefunden" und "kein Drive-Ordner hinterlegt".
    reason = backendMessage || 'Die Baustellendokumentation wurde für dieses Projekt nicht gefunden.';
  } else if (status === 503 || status === undefined) {
    reason = typeof navigator !== 'undefined' && navigator.onLine === false
      ? 'Keine Internetverbindung. Bitte Verbindung prüfen und erneut versuchen.'
      : 'Google Drive ist gerade nicht erreichbar. Bitte in einer Minute erneut versuchen.';
  } else {
    reason = backendMessage || 'Beim Laden der Baustellendokumentation ist ein Fehler aufgetreten.';
  }

  return `${reason} (${buildReference(context)})`;
};
